import { EMPLOYEE_ROLE_LABELS } from "@etudescarre/constants";
import { Button, Drawer, Select } from "@etudescarre/ui";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { toaster } from "@/app";
import { apiClient } from "@/lib/api/client";

type UserEditFormData = {
  firstName: string;
  lastName: string;
  email: string;
  role: "user" | "root";
};

type User = {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  role: "user" | "admin" | "root" | null;
};

export type UserEditFormProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: User;
  onSuccess?: () => void;
};

export function UserEditForm({
  open,
  onOpenChange,
  user,
  onSuccess,
}: UserEditFormProps) {
  const {
    register,
    handleSubmit,
    formState: { isSubmitting },
    reset,
    setValue,
    watch,
  } = useForm<UserEditFormData>({
    defaultValues: {
      firstName: user.firstName ?? "",
      lastName: user.lastName ?? "",
      email: user.email ?? "",
      role: user.role === "root" ? "root" : "user",
    },
  });

  const role = watch("role");

  // Reset form when user changes or drawer opens
  useEffect(() => {
    if (open) {
      reset({
        firstName: user.firstName ?? "",
        lastName: user.lastName ?? "",
        email: user.email ?? "",
        role: user.role === "root" ? "root" : "user",
      });
    }
  }, [open, user, reset]);

  const onSubmit = async (data: UserEditFormData) => {
    try {
      const { error } = await apiClient.PATCH("/api/admin/users/{userId}", {
        params: { path: { userId: user.id } },
        body: {
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          role: data.role,
        },
      });

      if (error) {
        toaster.error({
          title: "Erreur",
          description:
            error.error || "Echec de la modification de l'utilisateur",
        });
        return;
      }

      toaster.success({
        title: "Succes",
        description: "Utilisateur modifie avec succes",
      });

      onSuccess?.();
      onOpenChange(false);
    } catch {
      toaster.error({
        title: "Erreur",
        description: "Une erreur est survenue",
      });
    }
  };

  const roleOptions = [
    { label: EMPLOYEE_ROLE_LABELS.user, value: "user" },
    { label: "Root (Admin)", value: "root" },
  ];

  return (
    <Drawer.Root
      open={open}
      onOpenChange={(details) => onOpenChange(details.open)}
    >
      <Drawer.Portal>
        <Drawer.Backdrop />
        <Drawer.Positioner>
          <Drawer.Content>
            <div className="flex items-center justify-between border-b border-zinc-200 p-6 dark:border-zinc-800">
              <div>
                <Drawer.Title>Modifier l'utilisateur</Drawer.Title>
                <Drawer.Description>
                  Modifiez les informations de l'utilisateur
                </Drawer.Description>
              </div>
              <Drawer.CloseTrigger />
            </div>

            <form
              onSubmit={handleSubmit(onSubmit)}
              className="flex min-h-0 flex-1 flex-col"
            >
              <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-6">
                {/* First Name Input */}
                <div>
                  <label
                    htmlFor="firstName"
                    className="mb-1 block text-sm font-medium text-zinc-900 dark:text-zinc-100"
                  >
                    Prénom <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="firstName"
                    type="text"
                    {...register("firstName")}
                    required
                    className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100"
                    placeholder="Jean"
                  />
                </div>

                {/* Last Name Input */}
                <div>
                  <label
                    htmlFor="lastName"
                    className="mb-1 block text-sm font-medium text-zinc-900 dark:text-zinc-100"
                  >
                    Nom <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="lastName"
                    type="text"
                    {...register("lastName")}
                    required
                    className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100"
                    placeholder="Dupont"
                  />
                </div>

                {/* Email Input */}
                <div>
                  <label
                    htmlFor="email"
                    className="mb-1 block text-sm font-medium text-zinc-900 dark:text-zinc-100"
                  >
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="email"
                    type="email"
                    {...register("email")}
                    required
                    className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100"
                    placeholder="email@example.com"
                  />
                </div>

                {/* Role Select */}
                <div>
                  <span className="mb-1 block text-sm font-medium text-zinc-900 dark:text-zinc-100">
                    Role systeme <span className="text-red-500">*</span>
                  </span>
                  <Select
                    options={roleOptions}
                    value={[role]}
                    onValueChange={(details) => {
                      const val = details.value[0];
                      setValue("role", val === "root" ? "root" : "user");
                    }}
                    placeholder="Selectionner un role"
                  />
                  <p className="mt-1 text-xs text-zinc-500">
                    Root permet d'acceder a l'administration complete
                  </p>
                </div>
              </div>

              {/* Footer Actions */}
              <div className="flex shrink-0 justify-end gap-3 border-t border-zinc-200 p-6 dark:border-zinc-800">
                <Button
                  type="button"
                  outline
                  onClick={() => onOpenChange(false)}
                  disabled={isSubmitting}
                >
                  Annuler
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "En cours..." : "Modifier"}
                </Button>
              </div>
            </form>
          </Drawer.Content>
        </Drawer.Positioner>
      </Drawer.Portal>
    </Drawer.Root>
  );
}
